import { useState } from 'react';
import { useAtomValue } from 'jotai';
import T from '@wojtekmaj/react-t';

import Fieldset from '@rewardopl/react-ui/fieldset';
import RadioGroup from '@rewardopl/react-ui/radio_group';

import { Label, Value, Wrapper } from './index.styles';

import AddressField from '../address_field';

import { formatAddress } from '../../../utils/helpers';

import { maybeCurrentBusinessState } from '../../../store';

import type { Address } from '@rewardopl/types/helpers/address';

type AddressFieldWithDefaultProps = React.ComponentPropsWithoutRef<typeof AddressField>;

export default function AddressFieldWithDefault(props: AddressFieldWithDefaultProps) {
  const { label, name, onChange, required = true, value } = props;

  const maybeCurrentBusiness = useAtomValue(maybeCurrentBusinessState);

  const isBusinessAndHasBillingAddress = maybeCurrentBusiness?.billing_address;
  const isDefaultValue =
    isBusinessAndHasBillingAddress &&
    (!value || JSON.stringify(value) === JSON.stringify(maybeCurrentBusiness.billing_address));
  const [mode, setMode] = useState(isDefaultValue ? 'default' : 'custom');
  const [customAddress, setCustomAddress] = useState<Partial<Address> | undefined>(value);

  function onChangeMode(nextMode: string) {
    setMode(nextMode);

    onChange(
      nextMode === 'default' && maybeCurrentBusiness?.billing_address
        ? maybeCurrentBusiness.billing_address
        : customAddress,
    );
  }

  function onChangeCustomAddress(nextCustomAddress: Partial<Address> | undefined) {
    setCustomAddress(nextCustomAddress);

    if (mode === 'custom') {
      onChange(nextCustomAddress);
    }
  }

  if (!isBusinessAndHasBillingAddress) {
    return <AddressField {...props} />;
  }

  const modeOptions = [
    {
      label: (
        <>
          <Label>
            <T>Business address</T>
          </Label>
          <Value>{formatAddress(maybeCurrentBusiness.billing_address)}</Value>
        </>
      ),
      value: 'default',
    },
    {
      label: (
        <Label>
          <T>Other address</T>
        </Label>
      ),
      value: 'custom',
    },
  ];

  return (
    <Fieldset label={label} required={required}>
      <Wrapper>
        <RadioGroup
          name={`${name}[mode]`}
          form="_private"
          label="Address type"
          options={modeOptions}
          onChange={onChangeMode}
          required={required}
          value={mode}
        />
        {mode === 'default' && (
          <>
            <input
              type="hidden"
              name={`${name}[address]`}
              value={maybeCurrentBusiness.billing_address?.address}
            />
            <input
              type="hidden"
              name={`${name}[postcode]`}
              value={maybeCurrentBusiness.billing_address?.postcode}
            />
            <input
              type="hidden"
              name={`${name}[city]`}
              value={maybeCurrentBusiness.billing_address?.city}
            />
            <input
              type="hidden"
              name={`${name}[country]`}
              value={maybeCurrentBusiness.billing_address?.country}
            />
          </>
        )}
        {mode === 'custom' && (
          <AddressField
            label="Other address"
            name={name}
            onChange={onChangeCustomAddress}
            value={customAddress}
          />
        )}
      </Wrapper>
    </Fieldset>
  );
}
